import React, { useEffect, useMemo, useState } from 'react';
import { Box, Typography, Chip, Stack, ToggleButtonGroup, ToggleButton } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import dayjs from 'dayjs';
import { EQP_STATE_LABEL, getEqpStateStyle, formatSecAsHm, describeEventDetail } from './eqpState';

// 알람 막대는 val 이 ON 이라 상태 라벨 대신 '알람'으로 부른다.
const segLabel = (iv) => (iv.typ === 'ALARM' ? '알람' : (EQP_STATE_LABEL[iv.val] || iv.val));

const DAY_MIN = 1440; // 하루 전체를 분 단위(0~1440)로 다룬다

const minutesOf = (dt, dayStart) => Math.min(DAY_MIN, Math.max(0, dayjs(dt).diff(dayStart, 'minute', true)));

const SUMMARY_ITEMS = [
  { key: 'runSec', label: '운전' },
  { key: 'holdSec', label: '일시정지' },
  { key: 'idleSec', label: '정지' },
  { key: 'alarmSec', label: '알람' },
  { key: 'emergencySec', label: '비상정지' },
  { key: 'offlineSec', label: '오프라인' },
  { key: 'noDataSec', label: '데이터없음' },
];

// 00~24시 하루 타임라인. 위 막대는 STATE 구간, 아래 얇은 띠는 ALARM 구간.
// 가로를 누르면(데스크톱 호버는 <title> 로 기본 툴팁, 모바일은 탭) 아래쪽에 구간 상세를 보여 준다.
// "데이터 구간/하루 전체" 토글 - 오늘 날짜는 데이터가 쌓인 시간만 보면 눈금이 너무 넓어 보여서
// 기본으로 그 구간만 확대해서 보여 주고(zoomRange, 시간대별 차트와 동일한 범위), 누르면
// 00~24시 전체로 되돌릴 수 있게 한다. 지난 날짜는 범위 자체가 이미 00~23이라 토글이 사실상
// 의미 없지만(둘 다 같은 화면), 화면 구조를 통일하려고 그대로 둔다.
const EqpDayTimeline = ({ date, intervals = [], summary, loading, isToday, zoomRange }) => {
  const theme = useTheme();
  const [selected, setSelected] = useState(null);
  const [fullDay, setFullDay] = useState(!isToday);
  const dayStart = useMemo(() => dayjs(date).startOf('day'), [date]);
  const dayEnd = useMemo(() => dayStart.add(1, 'day'), [dayStart]);
  const dateKey = dayStart.format('YYYY-MM-DD');

  // 날짜가 바뀌면 토글을 그 날짜의 기본값으로 리셋한다(오늘=데이터 구간, 지난날=하루 전체)
  useEffect(() => { setFullDay(!isToday); }, [dateKey, isToday]);

  const { startHour, endHour } = zoomRange || { startHour: 0, endHour: 23 };
  const windowStartMin = fullDay ? 0 : startHour * 60;
  const windowEndMin = fullDay ? DAY_MIN : Math.min(DAY_MIN, (endHour + 1) * 60);
  const windowLen = Math.max(1, windowEndMin - windowStartMin);

  const toBar = (iv) => {
    const stMin = minutesOf(iv.stDt, dayStart);
    const edMin = iv.edDt ? minutesOf(iv.edDt, dayStart) : minutesOf(dayEnd, dayStart);
    const clSt = Math.max(stMin, windowStartMin);
    const clEd = Math.min(edMin, windowEndMin);
    return { ...iv, x: clSt - windowStartMin, w: Math.max(0, clEd - clSt) };
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps -- toBar 는 매 렌더 새로 만들어지는 순수 함수라 deps 에서 뺀다
  const stateBars = useMemo(
    () => intervals.filter((iv) => iv.typ === 'STATE').map(toBar).filter((b) => b.w > 0),
    [intervals, dayStart, windowStartMin, windowEndMin],
  );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const alarmBars = useMemo(
    () => intervals.filter((iv) => iv.typ === 'ALARM').map(toBar).filter((b) => b.w > 0),
    [intervals, dayStart, windowStartMin, windowEndMin],
  );

  // 눈금 간격 - 보이는 구간이 6시간 이하면 1시간마다, 아니면 2시간마다(하루 전체 기본값과 동일)
  const tickStepMin = windowLen <= 360 ? 60 : 120;
  const hourTicks = useMemo(() => {
    const ticks = [];
    for (let t = windowStartMin; t < windowEndMin; t += tickStepMin) ticks.push(t);
    if (ticks[ticks.length - 1] !== windowEndMin) ticks.push(windowEndMin); // 끝 경계는 항상 표시
    return ticks;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [windowStartMin, windowEndMin, tickStepMin]);

  // 가동 비율(수집 기준, 보조 지표) 분모 = 운전+일시정지+정지+알람+비상정지(오프라인·데이터없음 제외) -
  // 둘 다 "장비가 뭘 했는지 모른다"라 가동/비가동 어느 쪽에도 안 넣는다. board 카드·시간대별 차트
  // (hourlyUtilPct)와 같은 공식.
  const utilDenomSec = summary
    ? (Number(summary.runSec) || 0) + (Number(summary.holdSec) || 0) + (Number(summary.idleSec) || 0)
      + (Number(summary.alarmSec) || 0) + (Number(summary.emergencySec) || 0)
    : 0;
  const utilPct = summary && utilDenomSec > 0
    ? ((Number(summary.runSec) || 0) / utilDenomSec * 100).toFixed(1)
    : null;

  // 2026-10-23 "기준시간 가동률" 계획 - 주 지표. summary.plannedUtilPct가 null이면 그 날 기준시간이
  // 0(휴일/비근무요일)이라 "해당없음"이다 - runSec은 그대로 있을 수 있다(기준시간 밖 가동).
  const hasPlannedUtil = summary && summary.plannedUtilPct != null;
  const plannedUtilValue = hasPlannedUtil ? Number(summary.plannedUtilPct) : 0;
  const plannedUtilLabel = hasPlannedUtil ? `가동률(기준시간) ${summary.plannedUtilPct}%` : '가동률(기준시간) 해당없음';

  const renderSeg = (iv, idx, h, y, isAlarm) => {
    const style = getEqpStateStyle(theme, iv.val, false);
    const fill = isAlarm ? theme.palette.error.main : style.main;
    return (
      <rect
        key={`${iv.typ}-${idx}`}
        x={iv.x} y={y} width={iv.w} height={h}
        fill={fill}
        onClick={() => setSelected(iv)}
        style={{ cursor: 'pointer' }}
      >
        <title>
          {`${segLabel(iv)} · ${dayjs(iv.stDt).format('HH:mm')}~${iv.edDt ? dayjs(iv.edDt).format('HH:mm') : '진행중'}`}
        </title>
      </rect>
    );
  };

  if (loading && intervals.length === 0) {
    return <Typography variant="body2" color="text.secondary">불러오는 중...</Typography>;
  }

  return (
    <Box>
      <Stack direction="row" justifyContent="flex-end" sx={{ mb: 0.5 }}>
        <ToggleButtonGroup
          size="small" exclusive value={fullDay ? 'full' : 'data'}
          onChange={(_, v) => v && setFullDay(v === 'full')}
        >
          <ToggleButton value="data" sx={{ px: 1, py: 0.25, fontSize: 11 }}>데이터 구간</ToggleButton>
          <ToggleButton value="full" sx={{ px: 1, py: 0.25, fontSize: 11 }}>하루 전체</ToggleButton>
        </ToggleButtonGroup>
      </Stack>

      <Box sx={{ width: '100%', overflowX: 'auto' }}>
        <svg viewBox={`0 0 ${windowLen} 56`} width="100%" height="56" preserveAspectRatio="none" style={{ display: 'block', minWidth: 320 }}>
          {/* 시간 눈금선 */}
          {hourTicks.map((t) => (
            <line key={t} x1={t - windowStartMin} y1={0} x2={t - windowStartMin} y2={36} stroke={theme.palette.divider} strokeWidth={1} />
          ))}
          {/* 상태 막대 배경 + 구간 */}
          <rect x={0} y={0} width={windowLen} height={28} fill={theme.palette.grey[100]} />
          {stateBars.map((iv, i) => renderSeg(iv, i, 28, 0, false))}
          {/* 알람 막대(얇게, 상태 막대 아래) */}
          <rect x={0} y={32} width={windowLen} height={8} fill={theme.palette.grey[50]} />
          {alarmBars.map((iv, i) => renderSeg(iv, i, 8, 32, true))}
        </svg>
      </Box>
      <Box sx={{ position: 'relative', height: 16, px: 0.25 }}>
        {hourTicks.map((t) => (
          <Typography
            key={t} variant="caption" color="text.secondary"
            sx={{
              position: 'absolute',
              left: `${((t - windowStartMin) / windowLen) * 100}%`,
              transform: t === windowEndMin ? 'translateX(-100%)' : (t === windowStartMin ? 'none' : 'translateX(-50%)'),
            }}
          >
            {String(Math.round(t / 60)).padStart(2, '0')}
          </Typography>
        ))}
      </Box>

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, minHeight: 18 }}>
        {selected
          ? `${segLabel(selected)} · ${dayjs(selected.stDt).format('HH:mm:ss')} ~ ${selected.edDt ? dayjs(selected.edDt).format('HH:mm:ss') : '진행중'}`
            + (selected.edDt ? ` (${formatSecAsHm(dayjs(selected.edDt).diff(selected.stDt, 'second'))})` : '')
            + (describeEventDetail(selected.typ, selected.val, selected.detail) ? ` · ${describeEventDetail(selected.typ, selected.val, selected.detail)}` : '')
          : '막대를 누르면 구간 상세가 여기 보입니다.'}
      </Typography>

      {summary && (
        <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 1, rowGap: 1 }}>
          <Chip size="small" color={plannedUtilValue > 100 ? 'warning' : 'success'} label={plannedUtilLabel} />
          {summary.stdHr != null && (
            <Chip size="small" variant="outlined" label={`기준시간 ${summary.stdHr}h`} />
          )}
          {Number(summary.extraRunSec) > 0 && (
            <Chip size="small" variant="outlined" color="warning"
                  label={`기준시간 밖 가동 ${formatSecAsHm(summary.extraRunSec)}`} />
          )}
          {SUMMARY_ITEMS.map(({ key, label }) => (
            <Chip key={key} size="small" variant="outlined" label={`${label} ${formatSecAsHm(summary[key])}`} />
          ))}
          {utilPct != null && <Chip key="collected" size="small" label={`가동 비율(수집 기준) ${utilPct}%`} />}
        </Stack>
      )}
    </Box>
  );
};

export default EqpDayTimeline;
